import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  AddPlus2,
  CheckMark,
  ChevronDown,
  ChevronUp,
  PromoCodeTag,
  PromotionStepComplete,
  PromotionStepTwo,
} from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import {
  useCreatePromoCodeMutation,
  useDeletePromoCodeMutation,
  useFetchPromoCodesQuery,
  usePausePromoCodeMutation,
  usePublishPromoCodeMutation,
  useResumePromoCodeMutation,
  useUpdatePromoCodeMutation,
} from '../../../services/promoCode.api';
import {
  PromoCode,
  PromoCodeDraft,
  PromoCodeErrors,
  PromoCodesSectionProps,
  PromoCodeStatus,
} from '../../../stores/types/promoCode.interface';
import { ComputedPromotionStatus } from '../../../stores/types/promotions.interface';
import {
  Button,
  ButtonSize,
  ButtonType,
  ColorType,
  Icon,
  Label,
  LabelType,
  Spinner,
} from '../../_ui';
import { toValidDate } from '../promotion.utils';
import { useConfirmActionModal } from '../useConfirmActionModal';
import { PromoCodeCard } from './PromoCodeCard.component';
import { PromoCodeSavedRow } from './PromoCodeSavedRow.component';
import { PROMO_CODE_ADDABLE_PROMOTION_STATUSES } from './promoCode.constants';
import {
  buildCreatePromoCodePayload,
  buildUpdatePromoCodePayload,
  hasPromoCodeErrors,
  validatePromoCodeDraft,
} from './promoCode.utils';
import { usePromoCodeCardState } from './usePromoCodeCardState';

// Passed to setErrors to clear a card's validation errors.
const NO_ERRORS: PromoCodeErrors = {};

const createLocalId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `local-${Date.now()}-${Math.random().toString(36).slice(2)}`;

// A 409 means the code already exists. We surface this inline on the field rather
// than as a generic toast, so the user can fix the code without losing the draft.
const isDuplicateCodeError = (error: unknown): boolean => {
  const status = (error as { status?: number })?.status;
  return status === 409;
};

export const PromoCodesSection = ({
  promotion,
  companyId,
  canManage,
  autoAddCode,
  onValidationChange,
}: PromoCodesSectionProps) => {
  const { t } = useTranslation();
  const confirmAction = useConfirmActionModal();

  const [isExpanded, setIsExpanded] = useState(true);

  // Not-yet-saved draft codes (create flow), keyed by a generated localId.
  const [drafts, setDrafts] = useState<PromoCodeDraft[]>([]);
  const {
    savingId: createSavingId,
    setSavingId: setCreateSaving,
    setErrors: setCreateErrors,
    clearError: clearCreateError,
    setExpanded: setCreateExpanded,
    toggleExpanded: toggleCreateExpanded,
    reset: resetCreateCard,
    getErrors: getCreateErrors,
    isExpanded: isCreateExpanded,
  } = usePromoCodeCardState();

  // In-flight edits of saved DRAFT codes, keyed by promoCodeId. Presence of an entry
  // means that saved row is currently shown as an editable card.
  const [editDraftsById, setEditDraftsById] = useState<Record<string, PromoCodeDraft>>({});
  const {
    savingId: editSavingId,
    setSavingId: setEditSaving,
    setErrors: setEditErrors,
    clearError: clearEditError,
    setExpanded: setEditExpanded,
    toggleExpanded: toggleEditExpanded,
    reset: resetEditCard,
    getErrors: getEditErrors,
    isExpanded: isEditExpanded,
  } = usePromoCodeCardState();

  const { data: promoCodesData, isLoading: isPromoCodesLoading } = useFetchPromoCodesQuery(
    {
      companyId,
      'filter_eq[promotionId]': promotion?.promotionId ?? '',
    },
    { skip: !promotion || !companyId },
  );

  const [triggerCreate] = useCreatePromoCodeMutation();
  const [triggerUpdate] = useUpdatePromoCodeMutation();
  const [triggerDelete] = useDeletePromoCodeMutation();
  const [triggerPublish] = usePublishPromoCodeMutation();
  const [triggerPause] = usePausePromoCodeMutation();
  const [triggerResume] = useResumePromoCodeMutation();

  // Defensive scoping: only show codes that actually belong to this promotion. The
  // request is filtered by promotionId server-side, but we re-filter here so that a
  // missing/ignored server filter can never leak another promotion's codes. Memoized so
  // a sibling draft keystroke doesn't rebuild the array (and thus re-render every row).
  const savedCodes = useMemo(() => {
    const scoped = (promoCodesData?.entities ?? []).filter(
      (code) => code.promotionId === promotion?.promotionId,
    );
    // The API returns codes oldest-first (creation order); we show them newest-first so
    // the most recently added code is "Code 1".
    return [...scoped].reverse();
  }, [promoCodesData, promotion?.promotionId]);

  const savedCodeValidationErrors = useMemo(() => {
    if (!promotion) return {};
    return savedCodes.reduce<Record<string, PromoCodeErrors>>((acc, promoCode) => {
      acc[promoCode.promoCodeId] =
        promoCode.status === PromoCodeStatus.DRAFT
          ? validatePromoCodeDraft(promoCode, promotion, t)
          : NO_ERRORS;
      return acc;
    }, {});
  }, [savedCodes, promotion, t]);

  useEffect(() => {
    const hasErrors = savedCodes.some((promoCode) =>
      hasPromoCodeErrors(savedCodeValidationErrors[promoCode.promoCodeId] ?? NO_ERRORS),
    );
    onValidationChange?.(hasErrors);
  }, [savedCodeValidationErrors, savedCodes, onValidationChange]);

  const hasSavedCodes = savedCodes.length > 0;
  const isEmpty = savedCodes.length === 0 && drafts.length === 0;

  const canAddCode =
    canManage && !!promotion && PROMO_CODE_ADDABLE_PROMOTION_STATUSES.includes(promotion.status);

  const removeDraft = useCallback(
    (localId: string) => {
      setDrafts((prev) => prev.filter((draft) => draft.localId !== localId));
      resetCreateCard(localId);
    },
    [resetCreateCard],
  );

  const handleAddCode = useCallback(() => {
    const localId = createLocalId();
    setDrafts((prev) => [...prev, { localId, code: '', validFrom: null, validUntil: null }]);
    setCreateExpanded(localId, true);
  }, [setCreateExpanded]);

  // Arriving via the "Add code" deep-link opens a fresh draft card automatically (once),
  // so the user lands directly on the code form instead of an empty section.
  const autoAddedRef = useRef(false);
  useEffect(() => {
    if (autoAddCode && canAddCode && !autoAddedRef.current) {
      autoAddedRef.current = true;
      handleAddCode();
    }
  }, [autoAddCode, canAddCode, handleAddCode]);

  const handleDraftChange = useCallback(
    (localId: string, updater: (prev: PromoCodeDraft) => PromoCodeDraft) => {
      setDrafts((prev) =>
        prev.map((draft) => (draft.localId === localId ? updater(draft) : draft)),
      );
    },
    [],
  );

  const handleSaveDraft = useCallback(
    async (draft: PromoCodeDraft) => {
      if (!promotion) return;

      const validationErrors = validatePromoCodeDraft(draft, promotion, t);
      if (hasPromoCodeErrors(validationErrors)) {
        setCreateErrors(draft.localId, validationErrors);
        return;
      }
      setCreateErrors(draft.localId, NO_ERRORS);
      setCreateSaving(draft.localId);

      try {
        await triggerCreate({
          companyId,
          payload: buildCreatePromoCodePayload(draft, promotion.promotionId),
        }).unwrap();
        removeDraft(draft.localId);
      } catch (error) {
        if (isDuplicateCodeError(error)) {
          setCreateErrors(draft.localId, { code: t('promo_code_unique_error') });
        }
        // Generic failures are surfaced by the base.api toast pipeline.
      } finally {
        setCreateSaving(null);
      }
    },
    [companyId, promotion, removeDraft, setCreateErrors, setCreateSaving, t, triggerCreate],
  );

  // ----- Editing an existing DRAFT code -----

  const startEdit = useCallback(
    (promoCode: PromoCode) => {
      const draft: PromoCodeDraft = {
        localId: promoCode.promoCodeId,
        code: promoCode.code,
        validFrom: toValidDate(promoCode.validFrom),
        validUntil: toValidDate(promoCode.validUntil),
      };

      setEditDraftsById((prev) => ({
        ...prev,
        [promoCode.promoCodeId]: draft,
      }));
      if (promotion) {
        const validationErrors = validatePromoCodeDraft(draft, promotion, t);
        setEditErrors(promoCode.promoCodeId, validationErrors);
      }
      setEditExpanded(promoCode.promoCodeId, true);
    },
    [promotion, setEditErrors, setEditExpanded, t],
  );

  const cancelEdit = useCallback(
    (promoCodeId: string) => {
      setEditDraftsById((prev) => {
        const next = { ...prev };
        delete next[promoCodeId];
        return next;
      });
      resetEditCard(promoCodeId);
    },
    [resetEditCard],
  );

  const handleEditChange = useCallback(
    (promoCodeId: string, updater: (prev: PromoCodeDraft) => PromoCodeDraft) => {
      setEditDraftsById((prev) => ({ ...prev, [promoCodeId]: updater(prev[promoCodeId]) }));
    },
    [],
  );

  const handleSaveEdit = useCallback(
    async (draft: PromoCodeDraft) => {
      if (!promotion) return;
      const promoCodeId = draft.localId;

      const validationErrors = validatePromoCodeDraft(draft, promotion, t);
      if (hasPromoCodeErrors(validationErrors)) {
        setEditErrors(promoCodeId, validationErrors);
        return;
      }
      setEditErrors(promoCodeId, NO_ERRORS);
      setEditSaving(promoCodeId);

      try {
        await triggerUpdate({
          promoCodeId,
          companyId,
          payload: buildUpdatePromoCodePayload(draft),
        }).unwrap();
        cancelEdit(promoCodeId);
      } catch (error) {
        if (isDuplicateCodeError(error)) {
          setEditErrors(promoCodeId, { code: t('promo_code_unique_error') });
        }
        // Generic failures are surfaced by the base.api toast pipeline.
      } finally {
        setEditSaving(null);
      }
    },
    [cancelEdit, companyId, promotion, setEditErrors, setEditSaving, t, triggerUpdate],
  );

  // Delete (DRAFT) and archive (PAUSED) hit the same DELETE endpoint; only the
  // confirmation copy and test ids differ.
  const confirmDeleteOrArchive = useCallback(
    (promoCodeId: string, variant: 'delete' | 'archive') => {
      const isArchive = variant === 'archive';
      confirmAction({
        title: t(isArchive ? 'promo_code_archive_modal_title' : 'promo_code_delete_modal_title'),
        message: t(
          isArchive ? 'promo_code_archive_modal_message' : 'promo_code_delete_modal_message',
        ),
        confirmLabel: t(isArchive ? 'archive' : 'delete'),
        confirmType: ButtonType.DESTRUCTIVE,
        iconColor: ColorType.NEGATIVE,
        dataTestId: isArchive ? 'ArchivePromoCode' : 'DeletePromoCode',
        onConfirm: async () => {
          await triggerDelete({ promoCodeId, companyId, isArchive }).unwrap();
        },
      });
    },
    [companyId, confirmAction, t, triggerDelete],
  );

  // Deleting a saved DRAFT happens from inside its edit card, so this is keyed by id to
  // match the card's onDelete(localId) contract.
  const handleDeleteSavedCode = useCallback(
    (promoCodeId: string) => confirmDeleteOrArchive(promoCodeId, 'delete'),
    [confirmDeleteOrArchive],
  );

  const handleArchiveSavedCode = useCallback(
    (promoCode: PromoCode) => confirmDeleteOrArchive(promoCode.promoCodeId, 'archive'),
    [confirmDeleteOrArchive],
  );

  const handlePublishSavedCode = useCallback(
    (promoCode: PromoCode) => {
      if (!promotion) return;

      const validationErrors = validatePromoCodeDraft(promoCode, promotion, t);
      if (hasPromoCodeErrors(validationErrors)) {
        setEditErrors(promoCode.promoCodeId, validationErrors);
        return;
      }

      confirmAction({
        title: t('promo_code_publish_modal_title'),
        message: t('promo_code_publish_modal_message'),
        confirmLabel: t('publish'),
        dataTestId: 'PublishPromoCode',
        onConfirm: async () => {
          await triggerPublish({
            promoCodeId: promoCode.promoCodeId,
            companyId,
            promotionId: promotion.promotionId,
          }).unwrap();
        },
      });
    },
    [companyId, confirmAction, promotion, t, triggerPublish],
  );

  const handlePauseSavedCode = useCallback(
    (promoCode: PromoCode) => {
      confirmAction({
        title: t('promo_code_pause_modal_title'),
        message: t('promo_code_pause_modal_message'),
        confirmLabel: t('pause_code'),
        dataTestId: 'PausePromoCode',
        onConfirm: async () => {
          await triggerPause({ promoCodeId: promoCode.promoCodeId, companyId }).unwrap();
        },
      });
    },
    [companyId, confirmAction, t, triggerPause],
  );

  const handleResumeSavedCode = useCallback(
    (promoCode: PromoCode) => {
      confirmAction({
        title: t('promo_code_resume_modal_title'),
        message: t('promo_code_resume_modal_message'),
        confirmLabel: t('resume_code'),
        dataTestId: 'ResumePromoCode',
        onConfirm: async () => {
          await triggerResume({ promoCodeId: promoCode.promoCodeId, companyId }).unwrap();
        },
      });
    },
    [companyId, confirmAction, t, triggerResume],
  );

  const isDisabled = !promotion;
  // Archived promotions use the dark check (matching the details header); otherwise the
  // blue "complete" check once codes exist, else the in-progress step number.
  const stepIcon =
    promotion?.status === ComputedPromotionStatus.DELETED
      ? CheckMark
      : hasSavedCodes
      ? PromotionStepComplete
      : PromotionStepTwo;

  const renderHeader = () => (
    <button
      type='button'
      className='flex flex-row items-center justify-between w-full min-h-[68px] px-5 py-4 bg-grey1 rounded-t-lg'
      onClick={() => !isDisabled && setIsExpanded((prev) => !prev)}
      disabled={isDisabled}
      aria-expanded={isExpanded && !isDisabled}
      data-testid='promoCodesHeader'
    >
      <div className='flex flex-row items-center gap-3'>
        <Icon src={stepIcon} size={IconSize.SIZE_24x24} />
        <div className='flex flex-row items-center gap-2'>
          <Label
            text={t('promo_codes')}
            type={LabelType.LABEL_M}
            color={isDisabled ? ColorType.GREY5 : ColorType.BLACK}
          />
          {isDisabled && (
            <>
              <Label text='—' type={LabelType.BODY3} color={ColorType.GREY5} />
              <Label
                text={t('save_promotion_details_first')}
                type={LabelType.BODY3}
                color={ColorType.GREY5}
              />
            </>
          )}
        </div>
      </div>
      <Icon
        src={isExpanded && !isDisabled ? ChevronUp : ChevronDown}
        size={IconSize.SIZE_24x24}
        color={ColorType.GREY5}
        alt={isExpanded ? t('icon_alt_collapse') : t('icon_alt_expand')}
      />
    </button>
  );

  // The section is inert until the promotion has been saved. Returning here also narrows
  // `promotion` to non-null for the rest of the render.
  if (!promotion) {
    return (
      <div
        className='border border-grey2 rounded-lg overflow-hidden opacity-60 pointer-events-none select-none'
        aria-disabled
        data-testid='promoCodesSection'
      >
        {renderHeader()}
      </div>
    );
  }

  return (
    <div className='border border-grey2 rounded-lg overflow-hidden' data-testid='promoCodesSection'>
      {renderHeader()}

      {isExpanded && (
        <div className='flex flex-col gap-4 px-5 py-5 bg-white'>
          <div className='flex flex-row items-start justify-between gap-3'>
            <div className='flex flex-col gap-1'>
              <Label text={t('code_details')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
              <Label
                text={t('code_details_description')}
                type={LabelType.BODY3}
                color={ColorType.GREY6}
              />
            </div>
            <Button
              label={t('add_code')}
              type={ButtonType.TERTIARY}
              size={ButtonSize.SMALL}
              icon={AddPlus2}
              iconColor={ColorType.GREY6}
              onClick={handleAddCode}
              disabled={!canAddCode}
              dataTestId='addPromoCode'
            />
          </div>

          {isPromoCodesLoading ? (
            <div className='flex items-center justify-center py-8'>
              <Spinner height='h-7' width='w-7' />
            </div>
          ) : isEmpty ? (
            <div className='flex flex-col items-center justify-center gap-2 py-10 border border-dashed border-grey2 rounded-lg'>
              <Icon src={PromoCodeTag} size={IconSize.SIZE_32x32} color={ColorType.GREY4} />
              <Label
                text={t('no_promo_codes_title')}
                type={LabelType.LABEL_M}
                color={ColorType.GREY6}
              />
              <Label
                text={t('no_promo_codes_description')}
                type={LabelType.BODY3}
                color={ColorType.GREY5}
              />
            </div>
          ) : (
            <div className='flex flex-col gap-3'>
              {savedCodes.map((promoCode, idx) => {
                const editDraft = editDraftsById[promoCode.promoCodeId];
                if (editDraft) {
                  return (
                    <PromoCodeCard
                      key={promoCode.promoCodeId}
                      index={idx + 1}
                      mode='edit'
                      draft={editDraft}
                      errors={getEditErrors(promoCode.promoCodeId)}
                      promotion={promotion}
                      isExpanded={isEditExpanded(promoCode.promoCodeId)}
                      isSaving={editSavingId === promoCode.promoCodeId}
                      onToggleExpand={toggleEditExpanded}
                      onChange={handleEditChange}
                      onClearError={clearEditError}
                      onSave={handleSaveEdit}
                      onCancel={cancelEdit}
                      onDelete={handleDeleteSavedCode}
                    />
                  );
                }
                return (
                  <PromoCodeSavedRow
                    key={promoCode.promoCodeId}
                    index={idx + 1}
                    promoCode={promoCode}
                    promotion={promotion}
                    canManage={canManage}
                    errors={savedCodeValidationErrors[promoCode.promoCodeId] ?? NO_ERRORS}
                    onEdit={startEdit}
                    onPublish={handlePublishSavedCode}
                    onPause={handlePauseSavedCode}
                    onResume={handleResumeSavedCode}
                    onArchive={handleArchiveSavedCode}
                  />
                );
              })}
              {drafts.map((draft, idx) => (
                <PromoCodeCard
                  key={draft.localId}
                  index={savedCodes.length + idx + 1}
                  draft={draft}
                  errors={getCreateErrors(draft.localId)}
                  promotion={promotion}
                  isExpanded={isCreateExpanded(draft.localId)}
                  isSaving={createSavingId === draft.localId}
                  onToggleExpand={toggleCreateExpanded}
                  onChange={handleDraftChange}
                  onClearError={clearCreateError}
                  onSave={handleSaveDraft}
                  onDelete={removeDraft}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
